import { TokenIcon } from "@/components/brand/TokenIcon";
import type { CollateralKey } from "@/data/fixtures";
import {
	COLLATERAL_KEYS,
	consensusForAsset,
	type ProviderQuoteRow,
} from "@/lib/oracle/price-providers";

function formatUsd(price: number | null): string {
	if (price == null || price <= 0) return "—";
	return `$${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function ProviderTable({
	asset,
	quotes,
	inConsensus,
}: {
	asset: CollateralKey;
	quotes: ProviderQuoteRow[];
	inConsensus: Set<string>;
}) {
	const byProvider = new Map<string, ProviderQuoteRow>();
	for (const q of quotes) {
		if (!byProvider.has(q.provider)) byProvider.set(q.provider, q);
	}
	const rows = [...byProvider.values()].sort((a, b) =>
		a.providerLabel.localeCompare(b.providerLabel),
	);

	return (
		<div className="card-surface overflow-hidden">
			<div className="flex items-center gap-2 border-b border-border/60 px-4 py-3">
				<TokenIcon token={asset} size="sm" plain />
				<p className="font-semibold">{asset} — external quotes</p>
			</div>
			<div className="overflow-x-auto px-4 py-2">
				<table className="w-full text-sm">
					<thead>
						<tr className="border-b text-left text-[11px] uppercase text-muted-foreground">
							<th className="pb-2 pr-4 font-medium">Provider</th>
							<th className="pb-2 pr-4 font-medium">USD price</th>
							<th className="pb-2 font-medium">Consensus</th>
						</tr>
					</thead>
					<tbody>
						{rows.length === 0 ? (
							<tr>
								<td
									colSpan={3}
									className="py-4 text-muted-foreground"
								>
									No quotes yet — run oracle-keeper or wait for browser
									fetch.
								</td>
							</tr>
						) : (
							rows.map((row) => (
								<tr
									key={row.provider}
									className="border-b border-border/40"
								>
									<td className="py-2.5 pr-4">{row.providerLabel}</td>
									<td className="py-2.5 pr-4 font-mono-amount">
										{formatUsd(row.price)}
									</td>
									<td className="py-2.5">
										{row.price != null &&
										inConsensus.has(row.provider) ? (
											<span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">
												In cluster
											</span>
										) : row.price != null ? (
											<span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
												Outlier
											</span>
										) : (
											<span className="text-xs text-muted-foreground">
												{row.error ?? "—"}
											</span>
										)}
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
		</div>
	);
}

export function ExternalProvidersSection({
	providerQuotes,
	keeperUpdatedAt,
}: {
	providerQuotes: Record<CollateralKey, ProviderQuoteRow[]>;
	keeperUpdatedAt?: number | null;
}) {
	return (
		<section className="space-y-4">
			<div>
				<p className="section-eyebrow">Transparency</p>
				<h2 className="text-lg font-bold">External price providers</h2>
				<p className="mt-1 max-w-3xl text-sm text-muted-foreground">
					Public market quotes for comparison only —{" "}
					<strong className="font-medium text-foreground">
						not used for vault CR, mint, or liquidation
					</strong>{" "}
					on Play or production. If XRC is degraded, the host keeper may submit
					a multi-API consensus (≥2 Play / ≥3 production, within ±1.5%).
					Provider snapshots from the keeper appear via{" "}
					<span className="font-mono-amount">
						oracle-provider-snapshots.json
					</span>
					{keeperUpdatedAt ? (
						<>
							{" "}
							(last keeper sync{" "}
							{new Date(keeperUpdatedAt).toLocaleString()}).
						</>
					) : (
						" (run oracle-keeper to populate)."
					)}
				</p>
			</div>

			<div className="space-y-3">
				{COLLATERAL_KEYS.map((asset) => {
					const quotes = providerQuotes[asset] ?? [];
					const { inConsensus } = consensusForAsset(quotes);
					return (
						<ProviderTable
							key={asset}
							asset={asset}
							quotes={quotes}
							inConsensus={inConsensus}
						/>
					);
				})}
			</div>

			<div className="rounded-xl border border-border bg-muted/20 px-4 py-3 text-xs text-muted-foreground">
				<strong className="text-foreground">Live reference only.</strong>{" "}
				Rows above are fetched in your browser (plus keeper snapshot merge)
				for transparency. Protocol mint / liquidation use on-chain prices
				after keeper consensus submission.
			</div>
		</section>
	);
}
